import { ChangeDetectorRef, Component, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  Calendar,
  CalendarApi,
  CalendarOptions,
  DateSelectArg,
  EventApi,
  EventClickArg,
  EventInput,
} from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import { DEPARTMENT_LIST } from './event-utils2';
import { NzModalService } from 'ng-zorro-antd/modal';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CalendarEventsService } from 'src/app/services/calendarEvents.service';
import { AddCalendarEventComponent } from './add-calendar-event/add-calendar-event.component';
import {
  catchError,
  forkJoin,
  of,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { FullCalendarComponent } from '@fullcalendar/angular';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import {
  mapBackendAppointmentDataToCalendarEvents,
  mapCalendarEventsToBackendAppointmentData,
  getMemberResourceIds,
  toMemberResourceId,
} from './event-utils';
import { PatientService } from 'src/app/services/patient.service';
import { ResourceInput, ResourceSourceInput } from '@fullcalendar/resource';
import {
  IAppointmentParticipant,
  IAppointmentSlot,
} from 'src/app/interfaces/Appointment/IAppointment';
import { AppointmentService } from 'src/app/services/appointment.service';
import { COLORS } from 'src/styles/colors';
import {
  IAppointmentPersonnel,
  IAppointmentReason,
  IAppointmentServiceType,
  IAppointmentStatus,
} from 'src/app/interfaces/Appointment/IAppointmentDropdownData';
import { SwalToastService } from 'src/app/services/swal.service';
import { IRolePersonel } from 'src/app/interfaces/Appointment/IAppointmentRoles';
import { ViewCalendarEventComponent } from './view-calendar-event/view-calendar-event.component';
import { LocaleService } from 'src/app/services/locale.service';
import elLocale from '@fullcalendar/core/locales/el';
import enLocale from '@fullcalendar/core/locales/en-gb';
import { UserService } from 'src/app/services/user.service';
import { AppointmentSignalService } from 'src/app/services/appointmentSignalR.service';
import { KeycloakService } from 'keycloak-angular';
import { PermissionService } from 'src/app/services/permission.service';

// Rows of the resource timeline: patients, or non-patient users (doctors, nurses, ...).
type ResourceMode = 'patients' | 'members';
type PatientResourceFilter = 'all' | 'in' | 'out';

// Both are resourceTimeline views. Separate view names let FullCalendar highlight the
// matching toolbar button (a custom button is active when its name equals the view name).
const RESOURCE_TIMELINE_VIEWS: Record<ResourceMode, string> = {
  patients: 'resourceTimeline',
  members: 'membersTimeline',
};

@Component({
  selector: 'app-appointments',
  templateUrl: './appointments.component.html',
  styleUrls: ['./appointments.component.css'],
  standalone: false,
})
export class AppointmentsComponent {
  @ViewChild('calendar') calendarElement!: FullCalendarComponent;
  public addCalentarEventComp = AddCalendarEventComponent;
  public viewCalentarEventComp = ViewCalendarEventComponent;

  viewModeIsList = false;

  calendarVisible = signal(true);
  calendarOptions = signal<CalendarOptions>({
    schedulerLicenseKey: 'CC-Attribution-NonCommercial-NoDerivatives',
    dayMinWidth: 150, // will cause horizontal scrollbars
    locale: this.localeService.getCurrentLocale()!,
    locales: [elLocale, enLocale], // available locales from fullCalendar
    plugins: [
      interactionPlugin,
      dayGridPlugin,
      timeGridPlugin,
      listPlugin,
      resourceTimelinePlugin,
      // resourceTimeGridPlugin,
      scrollGridPlugin,
    ],
    headerToolbar: {
      left: 'prev,next today mine,team', // remove mine,team when patient consent is developed
      center: 'title',
      right:
        'resourceTimeline,membersTimeline,dayViewButton,weekViewButton,monthViewButton,toggleViewMode,addEventButton', //,timeGridWeek,timeGridDay,resourceTimeGridFourDay',
    },
    buttonText: {
      today: this.translate.instant('Today'),
    },
    customButtons: {
      // remove mine,team when patient consent is developed
      mine: {
        text: '👤 ' + this.translate.instant('My Calendar'),
        click: () => this.setFetchScope('mine'),
      },
      team: {
        text: '👥 ' + this.translate.instant('Team Calendar'),
        click: () => this.setFetchScope('team'),
      },
      addEventButton: {
        text: '✚',
        click: () => this.openModalFun(true),
      },
      dayViewButton: {
        text: this.translate.instant('Day'),
        click: () => this.setCurrentView('day'),
      },
      weekViewButton: {
        text: this.translate.instant('Week'),
        click: () => this.setCurrentView('week'),
      },
      monthViewButton: {
        text: this.translate.instant('Month'),
        click: () => this.setCurrentView('month'),
      },
      toggleViewMode: {
        text: this.getToggleButtonIcon(),
        click: () => this.toggleViewModeClick(),
      },
      resourceTimeline: {
        text: '𖨆 ' + this.translate.instant('Patients'),
        click: () => this.showResourceTimeline('patients'),
      },
      membersTimeline: {
        text: '⚕ ' + this.translate.instant('Users'),
        click: () => this.showResourceTimeline('members'),
      },
    },
    resources: [], // Initialize resources (patients)
    resourceOrder: 'sortOrder', // tells FullCalendar how to sort resources
    // views: {
    //   resourceTimeGridFourDay: {
    //     type: 'resourceTimeGrid', // Base the view on resourceTimeGrid
    //     duration: { days: 2 }, // Custom duration of 4 days
    //     buttonText: '4 day', // Button label in the toolbar
    //   },
    // },

    views: {
      resourceTimeline: {
        type: 'resourceTimeline',
      },
      membersTimeline: {
        type: 'resourceTimeline',
        resourceAreaHeaderContent: this.translate.instant('Users'),
        resourceLabelContent: (arg) => {
          const label = document.createElement('div');
          label.className = 'member-resource-label';

          const name = document.createElement('span');
          name.textContent = arg.resource.title;
          label.appendChild(name);

          const roles: string[] = arg.resource.extendedProps['roles'] ?? [];
          if (roles.length) {
            const roleNames = document.createElement('small');
            roleNames.className = 'member-resource-roles';
            roleNames.textContent = roles.join(', ');
            label.appendChild(roleNames);
          }

          return { domNodes: [label] };
        },
      },
      listDay: {
        type: 'listDay',
      },
      listWeek: {
        type: 'listWeek',
      },
      listMonth: {
        type: 'listMonth',
      },
      dayGridDay: {
        type: 'dayGridDay',
      },
      dayGridWeek: {
        type: 'dayGridWeek',
      },
      dayGridMonth: {
        type: 'dayGridMonth',
      },
    },

    resourceAreaHeaderContent: this.translate.instant('Patients'), // this replaces the default column title 'Resources' of the resourceTimeline view and also translates it
    resourceLabelContent: (arg) => {
      const patientId = arg.resource.id;

      if (!this.canAccessPatientProfile(patientId)) {
        const label = document.createElement('span');
        label.textContent = arg.resource.title;
        return { domNodes: [label] };
      }

      const link = document.createElement('a');
      link.className = 'patient-resource-link';
      link.textContent = arg.resource.title;
      link.title = this.translate.instant('View patient information');
      link.setAttribute('role', 'button');
      link.tabIndex = 0;

      const navigateToProfile = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        this.goToPatientProfile(patientId);
      };

      link.addEventListener('click', navigateToProfile);
      link.addEventListener('keydown', (ev: KeyboardEvent) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          navigateToProfile(ev);
        }
      });

      return { domNodes: [link] };
    },
    initialView: 'resourceTimeline',
    // initialEvents: INITIAL_EVENTS, // alternatively, use the `events` setting to fetch from a feed
    weekends: true,
    editable: true,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: false,
    nowIndicator: true,
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      meridiem: false, // this shows AM/PM
      hour12: false, // this shows 00:00 instead of 12:00
    },
    displayEventTime: true, // shows start time
    // displayEventEnd: true, // shows end time
    eventDisplay: 'block',
    eventContent: (arg) => {
      const viewType = this.calendarElement?.getApi().view?.type;
      const wrapper = document.createElement('div');
      wrapper.style.display = 'flex';
      wrapper.style.alignItems = 'center';
      wrapper.style.gap = '6px';

      if (['dayGridMonth', 'dayGridWeek', 'dayGridDay'].includes(viewType)) {
        wrapper.innerHTML = `
            <span>🕒 <b>${arg.timeText}</b></span>
            <span>📅 <i>${arg.event.title}</i></span>
          `;
      } else if (viewType === 'listWeek') {
        wrapper.innerHTML = `<b>${arg.timeText}</b> <i>${arg.event.title}</i>`;
      } else {
        // Default (e.g. resourceTimeline)
        wrapper.innerHTML = `<i>${arg.event.title}</i>`;
      }

      return { domNodes: [wrapper] };
    },
    businessHours: true,
    resourceAreaWidth: '20%', //ResourceTimeline: Width of the resource area (patients). If not specified, defaults to 30%
    slotMinWidth: 100, //ResourceTimeline: Minimum width of the time slots in pixels. If not specified it auto-calculates
    slotDuration: '00:15:00', //ResourceTimeline: Duration of each time slot (15 minutes). If not specified, defaults to 30 minutes(00:30:00)
    slotLabelInterval: '00:15:00', //ResourceTimeline: Interval between slot labels (15 minutes). If not specified, defaults to 1 hour(01:00:00)
    scrollTime: new Date(new Date().getTime() - 60 * 60 * 1000)
      .toTimeString()
      .substring(0, 8), // //ResourceTimeline: Scroll to the current time (1 hour ago). If not specified, defaults to 06:00:00 (6am)
    eventAllow: (dropInfo, draggedEvent) => {
      if (this.isResourceTimelineView()) {
        const targetResourceId = dropInfo.resource?.id;

        // Block if dragged to another resource. An event can sit on several rows
        // (e.g. doctor and nurse), so any of its own rows is a valid target.
        const isOwnRow = !!draggedEvent
          ?.getResources()
          .some((resource) => resource.id === targetResourceId);
        if (!isOwnRow) {
          return false;
        }
      }

      return true; // allow everything else
    },
    datesSet: () => this.applyScopeButtonActive(), // remove when patient consent is developed
    select: this.handleDateSelect.bind(this), // triggers when you select a date range
    eventClick: this.handleEventClick.bind(this), // triggers when you click on an event
    eventsSet: this.handleEvents.bind(this), // triggers when events are set (added/removed)
    // update a remote database when these fire:
    eventAdd: this.handleEventAdd.bind(this), // triggers when an event is added
    eventChange: this.handleEventChange.bind(this), // triggers when an event is changed
    // eventRemove: this.handleEventRemove.bind(this),
    eventDrop: this.handleEventDrop.bind(this), // triggers when an event is dropped (drag&drop)
    eventResize: this.handleEventResize.bind(this), // triggers when an event is resized (drag&drop)
  });

  lastSelectedPeriod: 'week' | 'day' | 'month' = 'week';
  fetchScope: 'mine' | 'team' = 'mine'; // remove when patient consent is developed

  // remove when patient consent is developed
  private setFetchScope(scope: 'mine' | 'team') {
    if (this.fetchScope === scope) return;
    this.fetchScope = scope;

    this.fetchBackendData();

    // re-apply after FC rebuilds the toolbar
    setTimeout(() => this.applyScopeButtonActive(), 0);
  }

  // remove when patient consent is developed
  private applyScopeButtonActive() {
    const mineBtn = document.querySelector('.fc-mine-button');
    const teamBtn = document.querySelector('.fc-team-button');

    [mineBtn, teamBtn].forEach((btn) => {
      if (!btn) return;
      btn.classList.remove('fc-button-active');
      btn.setAttribute('aria-pressed', 'false');
    });

    const toActivate = this.fetchScope === 'mine' ? mineBtn : teamBtn;
    if (toActivate) {
      toActivate.classList.add('fc-button-active');
      toActivate.setAttribute('aria-pressed', 'true');
    }
  }

  // Patients and Users buttons: same timeline, different rows
  private showResourceTimeline(mode: ResourceMode) {
    const modeChanged = this.resourceMode !== mode;
    this.resourceMode = mode;

    this.calendarElement.getApi().changeView(RESOURCE_TIMELINE_VIEWS[mode]);

    // Day/week/month/toggle are custom buttons, so FullCalendar does not clear their active state
    const activeBtns = document.querySelectorAll(
      '.fc-dayViewButton-button, .fc-weekViewButton-button, .fc-monthViewButton-button, .fc-toggleViewMode-button',
    );
    activeBtns.forEach((btn) => {
      btn.classList.remove('fc-button-active');
      btn.setAttribute('aria-pressed', 'false');
    });

    if (!modeChanged) return;

    this.selectedResourceId = null;
    this.applyResources();
  }

  isResourceTimelineView(): boolean {
    const viewType = this.calendarElement?.getApi().view?.type;
    return !!viewType && Object.values(RESOURCE_TIMELINE_VIEWS).includes(viewType);
  }

  isPatientResourceTimelineView(): boolean {
    return (
      this.resourceMode === 'patients' &&
      this.calendarElement?.getApi().view?.type ===
        RESOURCE_TIMELINE_VIEWS.patients
    );
  }

  // Users rows follow the calendar scope: My Calendar shows only the logged-in user,
  // Team Calendar shows every user from DropdownData.users
  private get scopedMembers(): ResourceInput[] {
    if (this.fetchScope === 'team') return this.membersList;

    const currentUserId = this.keycloakService.getKeycloakInstance()
      ?.tokenParsed?.['sub'];
    if (!currentUserId) return [];

    const currentUserResourceId = toMemberResourceId(String(currentUserId));
    return this.membersList.filter(
      (member) => member.id === currentUserResourceId,
    );
  }

  // Rows the search select can filter on
  get searchableResources(): any[] {
    return this.resourceMode === 'members'
      ? this.scopedMembers
      : this.visiblePatientResources;
  }

  private get visiblePatientResources(): any[] {
    if (this.patientResourceFilter === 'all') {
      return this.originalPatientsList;
    }

    return this.originalPatientsList.filter(
      (patient: any) => patient.category === this.patientResourceFilter,
    );
  }

  // Pushes the active timeline's rows to the calendar, keeping the search filter if its row still exists
  private applyResources() {
    const allResources = this.searchableResources;
    const selectedResource = this.selectedResourceId
      ? allResources.find(
          (r: any) => r.id.toString() === this.selectedResourceId!.toString(),
        )
      : undefined;

    if (!selectedResource) {
      this.selectedResourceId = null;
    }

    this.calendarOptions.update((options) => ({
      ...options,
      resources: (selectedResource ? [selectedResource] : allResources).map(
        (r: any) => ({ ...r }), // new object references to force update
      ),
    }));
  }

  // Rows with an appointment slot ending between now and end of today go first, then alphabetical
  private sortByUpcomingToday(
    resources: any[],
    idsWithUpcomingAppointments: Set<string>,
  ): any[] {
    return [...resources]
      .sort((a, b) => {
        const aHas = idsWithUpcomingAppointments.has(a.id);
        const bHas = idsWithUpcomingAppointments.has(b.id);
        if (aHas === bHas) {
          return a.title.localeCompare(b.title);
        }
        return aHas ? -1 : 1;
      })
      .map((r, index) => ({ ...r, sortOrder: index })); //  add sortOrder for FullCalendar
  }

  private mapMemberToResource(user: IAppointmentPersonnel): ResourceInput {
    return {
      id: toMemberResourceId(user.id),
      title: user.fullName,
      extendedProps: {
        roles: this.getRoleNamesForUser(user.id),
      },
    };
  }

  // Role/specialty names listing this user in UsersByRoleDropdown (already loaded as rolesList)
  private getRoleNamesForUser(userId: string): string[] {
    const memberId = toMemberResourceId(userId);
    const listsUser = (users: { userId: string }[]) =>
      users.some((user) => toMemberResourceId(user.userId) === memberId);

    return this.rolesList.flatMap((role) => [
      ...(listsUser(role.users) ? [role.roleName] : []),
      ...role.subRoleUsers
        .filter((subRole) => listsUser(subRole.users))
        .map((subRole) => subRole.roleName),
    ]);
  }

  toggleViewModeClick() {
    this.viewModeIsList = !this.viewModeIsList;
    this.setCurrentView(this.lastSelectedPeriod);

    this.calendarOptions.update((options) => ({
      ...options,
      customButtons: {
        ...options.customButtons!,
        toggleViewMode: {
          ...options.customButtons!['toggleViewMode'],
          text: this.getToggleButtonIcon(),
        },
      },
    }));

    const toggleBtn = document.querySelector('.fc-toggleViewMode-button');
    if (toggleBtn) {
      toggleBtn.classList.remove('fc-button-active');
      toggleBtn.setAttribute('aria-pressed', 'false');
    }
  }

  setCurrentView(period: 'day' | 'week' | 'month') {
    ['day', 'week', 'month'].forEach((p) => {
      const btn = document.querySelector(`.fc-${p}ViewButton-button`);
      if (btn) {
        btn.classList.remove('fc-button-active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });

    this.lastSelectedPeriod = period;

    const calendarApi = this.calendarElement?.getApi();
    const viewName = this.viewModeIsList
      ? `list${this.capitalize(period)}`
      : `dayGrid${this.capitalize(period)}`;

    calendarApi?.changeView(viewName);

    const activeBtn = document.querySelector(`.fc-${period}ViewButton-button`);
    if (activeBtn) {
      activeBtn.classList.add('fc-button-active');
      activeBtn.setAttribute('aria-pressed', 'true');
    }
  }

  capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  getToggleButtonIcon(): string {
    return this.viewModeIsList ? '☰' : '𝄜';
  }

  currentEvents = signal<EventApi[]>([]);
  addEventsub!: Subscription;
  editEventsub!: Subscription;
  updateEventsub!: Subscription;
  deleteEventsub!: Subscription;
  calendarVisiblesub!: Subscription;
  tenantIdsub!: Subscription;
  tenantId: number | null = null;
  addEventForm!: FormGroup;

  appointments: EventInput[] = [];
  patientsList: ResourceInput = [];
  personelList: ResourceInput = [];
  departmentList: ResourceInput = [];
  recurrentTypeList: ResourceInput = [];

  appointmentStatusList: IAppointmentStatus[] = [];
  appointmentServiceTypeList: IAppointmentServiceType[] = [];
  appointmentReasonList: IAppointmentReason[] = [];
  slotStatusList: IAppointmentStatus[] = [];
  participantStatusList: IAppointmentStatus[] = [];

  rolesList: IRolePersonel[] = []; // Store full role data with users and sub-roles

  // Store event IDs that have already been updated (if this is not done the event will be updated multiple times due to calendarApi.updateEvent)
  private updatedEvents = new Set<string>();

  lastViewType = 'resourceTimeline';
  private lastDate: Date = new Date();

  originalPatientsList: any = [];
  patientResourceFilter: PatientResourceFilter = 'all';
  // Users (non-patient members) timeline rows, built from DropdownData.users
  resourceMode: ResourceMode = 'patients';
  membersList: ResourceInput[] = [];

  // Row filter of the search select; applies to the active timeline (patients or users)
  selectedResourceId: string | null = null;
  private accessiblePatientIds = new Set<string>();

  constructor(
    private changeDetector: ChangeDetectorRef,
    private modal: NzModalService,
    private fb: FormBuilder,
    private calendarEventService: CalendarEventsService,
    private translate: TranslateService,
    private patientService: PatientService,
    private appointmentService: AppointmentService,
    private swalToastService: SwalToastService,
    private localeService: LocaleService,
    private userService: UserService,
    private signalService: AppointmentSignalService,
    private readonly permissionService: PermissionService,
    private router: Router,
    private keycloakService: KeycloakService,
  ) {}

  private destroy$ = new Subject<void>();

  goToPatientProfile(patientId: string): void {
    if (!this.canAccessPatientProfile(patientId)) return;
    this.patientService.resetClinicalData();
    this.router.navigate(['/ehr/profile/' + patientId]);
  }

  canAccessPatientProfile(patientId: string): boolean {
    return (
      this.permissionService.can(['v-pcp']) &&
      this.accessiblePatientIds.has(String(patientId))
    );
  }

  ngOnInit() {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateCalendarTranslations());
    this.updateCalendarTranslations();

    this.addEventForm = this.fb.group({
      title: ['', [Validators.required]],
      start: [null, [Validators.required]],
      end: [null],
      allDay: [false],
    });
    this.addEventsub = this.calendarEventService.addEvent$.subscribe(
      (newEvent) => {
        if (newEvent) {
          this.addEventToCalendar(newEvent);
        }
      },
    );
    this.editEventsub = this.calendarEventService.editEvent$.subscribe(
      (editEvent) => {
        if (!editEvent) return;

        this.openEditModal(editEvent);
      },
    );
    this.updateEventsub = this.calendarEventService.updateEvent$.subscribe(
      (updatePayload) => {
        if (updatePayload?.event) {
          this.updateEventInCalendar(
            updatePayload.event,
            updatePayload.updateType,
          );
        }
      },
    );

    this.deleteEventsub = this.calendarEventService.deleteEvent$.subscribe(
      (deleteEvent) => {
        if (deleteEvent) {
          this.handleEventRemove(deleteEvent);
        }
      },
    );

    this.calendarVisiblesub = this.calendarEventService.calendarVisible$
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => {
        const calendarApi = this.calendarElement?.getApi();
        if (calendarApi) {
          this.lastViewType = calendarApi.view.type;
          this.lastDate = calendarApi.getDate();
        }
        this.changeDetector.detectChanges();
        this.calendarVisible.set(visible);
      });

    this.tenantIdsub = this.userService.tenantIdSub
      .asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenantId) => {
        this.tenantId = tenantId;
      });

    // this.signalService.startConnection(this.tenantId!);
    // console.log(this.tenantId);
    this.fetchBackendData();
  }

  private updateCalendarTranslations(): void {
    const appLocale =
      this.translate.currentLang ||
      this.localeService.getCurrentLocale() ||
      'en';
    const calendarLocale = appLocale.toLowerCase().startsWith('el')
      ? 'el'
      : 'en-gb';

    this.calendarOptions.update((options) => {
      const customButtons = options.customButtons ?? {};
      const views = options.views ?? {};

      return {
        ...options,
        locale: calendarLocale,
        buttonText: {
          ...options.buttonText,
          today: this.translate.instant('Today'),
        },
        customButtons: {
          ...customButtons,
          mine: {
            ...customButtons['mine'],
            text: '👤 ' + this.translate.instant('My Calendar'),
          },
          team: {
            ...customButtons['team'],
            text: '👥 ' + this.translate.instant('Team Calendar'),
          },
          dayViewButton: {
            ...customButtons['dayViewButton'],
            text: this.translate.instant('Day'),
          },
          weekViewButton: {
            ...customButtons['weekViewButton'],
            text: this.translate.instant('Week'),
          },
          monthViewButton: {
            ...customButtons['monthViewButton'],
            text: this.translate.instant('Month'),
          },
          resourceTimeline: {
            ...customButtons['resourceTimeline'],
            text: '𖨆 ' + this.translate.instant('Patients'),
          },
          membersTimeline: {
            ...customButtons['membersTimeline'],
            text: '⚕ ' + this.translate.instant('Users'),
          },
        },
        resourceAreaHeaderContent: this.translate.instant('Patients'),
        views: {
          ...views,
          resourceTimeline: {
            ...views['resourceTimeline'],
            resourceAreaHeaderContent: this.translate.instant('Patients'),
          },
          membersTimeline: {
            ...views['membersTimeline'],
            resourceAreaHeaderContent: this.translate.instant('Users'),
          },
        },
      };
    });

    setTimeout(() => this.applyScopeButtonActive(), 0);
  }

  ngAfterViewInit(): void {
    this.calendarVisiblesub = this.calendarEventService.calendarVisible$
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => {
        this.calendarVisible.set(visible);

        if (visible) {
          setTimeout(() => {
            const calendarApi = this.calendarElement?.getApi();
            if (calendarApi) {
              calendarApi.changeView(this.lastViewType, this.lastDate);
              calendarApi.updateSize();

              // remove when patient consent is developed
              // START OF REMOVE
              setTimeout(() => {
                const api = this.calendarElement?.getApi();
                if (api) {
                  api.changeView(this.lastViewType, this.lastDate);
                  api.updateSize();
                }
                this.applyScopeButtonActive(); // default highlight
              }, 100);
            }

            // default active: MINE // remove when patient consent is developed
            const mineBtn = document.querySelector('.fc-mine-button');
            if (mineBtn) {
              mineBtn.classList.add('fc-button-active');
              mineBtn.setAttribute('aria-pressed', 'true');
            }
            const teamBtn = document.querySelector('.fc-team-button');
            if (teamBtn) {
              teamBtn.classList.remove('fc-button-active');
              teamBtn.setAttribute('aria-pressed', 'false');
            }
          }, 100);

          // END OF REMOVE
          this.changeDetector.detectChanges();
        }
      });

    this.signalService.onAppointmentUpdated(() => {
      this.fetchBackendData();
    });
  }

  ngOnDestroy() {
    // this.signalService.stopConnection(this.tenantId!);

    // Clean up the subscription
    this.addEventsub.unsubscribe();
    this.editEventsub.unsubscribe();
    this.updateEventsub.unsubscribe();
    this.deleteEventsub.unsubscribe();
    this.calendarVisiblesub.unsubscribe();
    this.tenantIdsub.unsubscribe();

    // Close modals
    this.calendarEventService.addCalendarEventModalSub.next(false);
    this.calendarEventService.addCalendarEventModalData = {};
    this.calendarEventService.viewCalendarEventModalSub.next(false);
    this.calendarEventService.viewCalendarEventModalData = {};

    this.calendarEventService.addCalendarEventModalSub.complete();
    this.calendarEventService.addCalendarEventModalSub = new Subject<boolean>();

    this.calendarEventService.viewCalendarEventModalSub.complete();
    this.calendarEventService.viewCalendarEventModalSub =
      new Subject<boolean>();

    // reset signal
    this.calendarVisible.set(false);
  }

  fetchBackendData() {
    this.calendarEventService.setCalendarVisible(false);

    // Preload status observables (they are BehaviorSubjects, so already emit latest)
    this.appointmentService.appointmentStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((statuses) => (this.appointmentStatusList = statuses));
    this.appointmentService.slotStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((statuses) => (this.slotStatusList = statuses));
    this.appointmentService.participantStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((statuses) => (this.participantStatusList = statuses));

    // Trigger statuses manually once
    this.appointmentService.loadAppointmentStatuses();

    forkJoin({
      dropdownData: this.appointmentService.getDropdownData().pipe(
        catchError((err) => {
          this.swalToastService.toastError(
            `${this.translate.instant('Unable to load dropdown data')}!)`,
          );
          return of(null);
        }),
      ),
      patientResources: this.appointmentService.getPatientResources().pipe(
        catchError((err) => {
          this.swalToastService.toastError(
            `${this.translate.instant('Unable to load patient resources')}!)`,
          );
          return of(null);
        }),
      ),
      rolesData: this.appointmentService.getUsersByRoleDropdownData().pipe(
        catchError((err) => {
          this.swalToastService.toastError(
            `${this.translate.instant('Unable to load roles data')}!)`,
          );
          return of(null);
        }),
      ),
      appointments: this.appointmentService
        .getAppointments(this.fetchScope) // remove this.fetchScope when patient consent is developed
        .pipe(
          catchError((err) => {
            this.swalToastService.toastError(
              `${this.translate.instant('Unable to load appointments data')}!)`,
            );
            return of(null);
          }),
        ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ({ dropdownData, patientResources, rolesData, appointments }) => {
        if (dropdownData?.succeed && dropdownData.data) {
          // Personnel
          this.personelList = dropdownData.data.users.map((u) => ({
            id: u.id,
            title: u.fullName,
          }));

          // Recurrent Types
          this.recurrentTypeList = dropdownData.data.recurrentType.map(
            (type) => ({
              id: type.id,
              name: type.name,
            }),
          );
          // Service Types
          this.appointmentServiceTypeList = dropdownData.data.serviceTypes.map(
            (type) => ({
              id: type.id,
              name: type.name,
            }),
          );
          // Reasons
          this.appointmentReasonList = dropdownData.data.reasons.map(
            (type) => ({
              id: type.id,
              name: type.name,
            }),
          );
        }

        if (patientResources?.succeed && patientResources.data) {
          this.accessiblePatientIds = new Set(
            patientResources.data.map((patient) => String(patient.id)),
          );
          this.patientsList = patientResources.data
            .map((patient) => ({
              id: patient.id.toString(),
              title: patient.fullName,
              category: patient.category,
            }))
            .sort((a, b) => a.title.localeCompare(b.title));
          this.originalPatientsList = [...(this.patientsList as any[])];
        } else {
          this.accessiblePatientIds.clear();
          this.patientsList = [];
          this.originalPatientsList = [];
        }

        if (rolesData?.succeed && rolesData.data) {
          this.rolesList = rolesData.data;
        }

        // Users timeline rows: the same users that can be picked as appointment participants
        if (dropdownData?.succeed && dropdownData.data) {
          this.membersList = dropdownData.data.users
            .filter((user) => !!user.id)
            .map((user) => this.mapMemberToResource(user))
            .sort((a, b) => a.title!.localeCompare(b.title!));
        }

        if (appointments?.succeed && appointments.data) {
          const mappedEvents = mapBackendAppointmentDataToCalendarEvents(
            appointments.data,
          );

          // Inject events via FullCalendar's callback format
          this.calendarOptions.update((options) => ({
            ...options,
            events: (_info, successCallback, failureCallback) => {
              this.appointmentService
                .getAppointments(this.fetchScope) //remove this.fetchScope when patient consent is developed
                .subscribe({
                  next: (res) => {
                    if (res?.succeed && res.data) {
                      const mappedEvents =
                        mapBackendAppointmentDataToCalendarEvents(res.data);
                      successCallback(mappedEvents);
                    } else {
                      failureCallback(new Error(res.errorCode + res.message));
                    }
                  },
                  error: (err) => {
                    this.swalToastService.toastError(err.error.message);
                    failureCallback(err);
                  },
                });
            },
          }));
        }
        // Sort patients and users by upcoming appointment
        if (
          appointments?.succeed &&
          appointments.data &&
          Array.isArray(appointments.data)
        ) {
          const now = new Date();
          const endOfToday = new Date();
          endOfToday.setHours(23, 59, 59, 999);

          const upcomingTodayAppointments = appointments.data.filter((appt) =>
            appt.slots.some((slot: IAppointmentSlot) => {
              const slotEnd = new Date(slot.end);
              return slotEnd >= now && slotEnd <= endOfToday;
            }),
          );
          const upcomingTodayParticipants = upcomingTodayAppointments.flatMap(
            (appt) => appt.participants,
          );

          if (
            dropdownData?.succeed &&
            dropdownData.data &&
            Array.isArray(this.patientsList)
          ) {
            const patientIdsWithUpcomingAppointments = new Set(
              upcomingTodayParticipants
                .map((p: IAppointmentParticipant) =>
                  p.actor?.patientId?.toString(),
                )
                .filter((id: any): id is string => !!id),
            );

            const sortedPatients = this.sortByUpcomingToday(
              this.patientsList,
              patientIdsWithUpcomingAppointments,
            );

            // maintain backup
            this.patientsList = sortedPatients;
            this.originalPatientsList = [...sortedPatients];
          }

          if (dropdownData?.succeed && dropdownData.data) {
            const memberIdsWithUpcomingAppointments = new Set(
              upcomingTodayAppointments.flatMap(getMemberResourceIds),
            );

            this.membersList = this.sortByUpcomingToday(
              this.membersList,
              memberIdsWithUpcomingAppointments,
            );
          }
        }

        this.applyResources();

        this.calendarEventService.setCalendarVisible(true);
        setTimeout(() => this.applyScopeButtonActive(), 0); // remove when patient consent is developed
      },
      );
  }

  openModalFun(
    showModal: boolean,
    start?: Date,
    end?: Date,
    resourceId?: string,
  ) {
    const isMembersRow = this.resourceMode === 'members';
    this.calendarEventService.addCalendarEventModalData = {
      start: start,
      end: end,
      patients: [],
      personnel: this.personelList,
      recurrentType: this.recurrentTypeList,
      appointmentServiceTypeList: this.appointmentServiceTypeList,
      appointmentReasonList: this.appointmentReasonList,
      departments: DEPARTMENT_LIST,
      // a selected timeline row preselects that patient, or that user as personnel
      resourceId: isMembersRow ? undefined : resourceId,
      personnelId: isMembersRow ? resourceId : undefined,
    };
    this.calendarEventService.addCalendarEventModalSub.next(showModal);
  }

  openEditModal(event: EventApi) {
    // console.log('event', event);

    // Emit the event data to the add-calendar-event component
    this.calendarEventService.addCalendarEventModalData = {
      event: event,
      patients: [],
      personnel: this.personelList,
      recurrentType: this.recurrentTypeList,
      departments: DEPARTMENT_LIST,
      appointmentServiceTypeList: this.appointmentServiceTypeList,
      appointmentReasonList: this.appointmentReasonList,
    };

    this.calendarEventService.addCalendarEventModalSub.next(true);
  }

  // Add the new event to the calendar
  addEventToCalendar(event: any) {
    let calendarApi: Calendar = this.calendarElement.getApi();
    calendarApi.addEvent(event);
  }

  removeEventFromCalendar(event: EventApi) {
    event.remove();
  }

  updateEventInCalendar(event: any, updateType?: number) {
    this.handleEventChange(event, updateType);
  }

  handleCalendarToggle() {
    this.calendarEventService.toggleCalendarVisible();
  }

  handleWeekendsToggle() {
    this.calendarOptions.update((options) => ({
      ...options,
      weekends: !options.weekends,
    }));
  }

  handleDateSelect(selectInfo: DateSelectArg) {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // today at midnight
    const selectedDate = new Date(selectInfo.start);
    selectedDate.setHours(0, 0, 0, 0);

    // Block if selected date is before today
    if (selectedDate < now) {
      Swal.fire({
        icon: 'warning',
        title: this.translate.instant('Action Not Allowed'),
        text: this.translate.instant(
          'Appointments cannot be scheduled for past dates. Please select today or a future date to create a new appointment. To modify an existing appointment, please use the edit option.',
        ),
        confirmButtonColor: COLORS.green,
      });
      return;
    }
    const resourceId = selectInfo.resource?._resource.id ?? null;
    this.openModalFun(true, selectInfo.start, selectInfo.end, resourceId!);
  }

  handleEventClick(clickInfo: EventClickArg) {
    const event: EventApi = clickInfo.event;
    // console.log('Event clicked:', event);

    const patients = Array.isArray(event.extendedProps?.['patients'])
      ? event.extendedProps['patients'].map((patient: any) => ({
          ...patient,
          canAccessProfile: this.canAccessPatientProfile(patient.id),
        }))
      : [];

    const personnel = Array.isArray(event.extendedProps?.['personnel'])
      ? event.extendedProps['personnel']
      : [];

    const departments = Array.isArray(event.extendedProps?.['department'])
      ? event.extendedProps['department']
      : [];

    const recurrentType = event.extendedProps?.['recurrentTypeId'] || null;

    // Push data to modal observable
    this.calendarEventService.viewCalendarEventModalData = {
      event,
      patients,
      personnel,
      departments,
      recurrentType,
      appointmentStatusList: this.appointmentStatusList,
      appointmentServiceTypeList: this.appointmentServiceTypeList,
      appointmentReasonList: this.appointmentReasonList,
      slotStatusList: this.slotStatusList,
      participantStatusList: this.participantStatusList,
    };

    this.calendarEventService.viewCalendarEventModalSub.next(true);
  }

  handleEvents(events: EventApi[]) {
    this.currentEvents.set(events);
    this.changeDetector.detectChanges(); // workaround for pressionChangedAfterItHasBeenCheckedError
  }

  handleEventAdd(addedItem: any) {
    let event = addedItem.event;
    event = mapCalendarEventsToBackendAppointmentData(event, this.rolesList);
    this.appointmentService.newAppointment(event).subscribe({
      next: (response) => {
        this.fetchBackendData();
        this.swalToastService.toastSuccess(
          `${this.translate.instant('New appointment created successfully')}`,
        );
      },
      error: (err) => {
        // this.fetchBackendData();
        this.removeEventFromCalendar(event);
        this.swalToastService.toastError(
          `${this.translate.instant('Unable to create appointment')}! ${
            err.error.message
          }`,
        );
        this.fetchBackendData();
      },
    });
  }
  handleEventRemove(removedItem: any) {
    this.fetchBackendData();
  }

  handleEventChange(changedItem: any, updateType?: number) {
    let event = changedItem;

    if (!event.id) {
      return;
    }

    // Prevent duplicate updates
    if (this.updatedEvents.has(event.id)) {
      return;
    }

    this.updatedEvents.add(event.id); // Mark event as updated

    let backendEvent = mapCalendarEventsToBackendAppointmentData(
      event,
      this.rolesList,
    );
    // console.log('Backend Event:', backendEvent);

    const isRecurrent = event.extendedProps?.['recurrentTypeId'] !== 1;

    if (updateType) {
      if (updateType === 1) {
        backendEvent.recurrentTypeId = 1;
        backendEvent.recurrenceOccurrences = null;
      }
      this.appointmentService
        .updateAppointment(backendEvent.id, backendEvent, updateType)
        .subscribe({
          next: (response) => {
            this.updatedEvents.delete(event.id); //Remove event from tracking after successful update
            this.fetchBackendData();
            this.swalToastService.toastSuccess(
              `${this.translate.instant('Appointment updated successfully')}`,
            );
          },
          error: (err) => {
            this.updatedEvents.delete(event.id); //Ensure removal even if update fails
            // this.fetchBackendData();
            this.swalToastService.toastError(
              `${this.translate.instant('Unable to update appointment')}! ${
                err.error.message
              }`,
            );
            this.fetchBackendData();
          },
        });
    } else {
      this.appointmentService
        .updateAppointment(backendEvent.id, backendEvent)
        .subscribe({
          next: (response) => {
            this.updatedEvents.delete(event.id); //Remove event from tracking after successful update
            this.fetchBackendData();
            this.swalToastService.toastSuccess(
              `${this.translate.instant('Appointment updated successfully')}`,
            );
          },
          error: (err) => {
            this.updatedEvents.delete(event.id); //Ensure removal even if update fails
            // this.fetchBackendData();
            this.swalToastService.toastError(
              `${this.translate.instant('Unable to update appointment')}! ${
                err.error.message
              }`,
            );
            this.fetchBackendData();
          },
        });
    }
  }

  private checkAndHandleConflictBeforeUpdate(
    event: any,
    updateType: number,
    revertFn: () => void,
  ) {
    const backendEvent = mapCalendarEventsToBackendAppointmentData(
      event,
      this.rolesList,
    );

    this.appointmentService
      .checkForConflicts(backendEvent, event.id)
      .subscribe({
        next: (response) => {
          const conflictData = response.data;

          if (!conflictData.overbooked && conflictData.conflicts.length === 0) {
            this.handleEventChange(event, updateType);
          } else {
            this.calendarEventService.setCalendarVisible(true);
            const formatISO = (iso: string): { date: string; time: string } => {
              const [rawDate, timeWithZone] = iso.split('T');
              const [year, month, day] = rawDate.split('-');
              const [hour, minute] = timeWithZone.replace('Z', '').split(':');
              return {
                date: `${day}/${month}/${year}`,
                time: `${hour}:${minute}`,
              };
            };

            const conflictText = conflictData.conflicts
              .map((c) => {
                const start = formatISO(c.start);
                const end = formatISO(c.end);
                return `
                <div style="margin-bottom: 18px; text-align: left;">
                  <div><b>${this.translate.instant('Slot')}:</b></div>
                  <div style="margin-left: 16px; margin-top: 6px;">
                    <div><b>${this.translate.instant('Start')}:</b> ${
                      start.date
                    }, ${start.time}</div>
                    <div><b>${this.translate.instant('End')}:</b> ${
                      end.date
                    }, ${end.time}</div>
                  </div>
                  <br>
                  <div><b>${this.translate.instant('Participants')}:</b></div>
                  <div style="margin-left: 16px; margin-top: 6px;">
                    ${c.conflictingNames
                      .map((name) => `<div>${name}</div>`)
                      .join('')}
                  </div>
                </div>
              `;
              })
              .join(
                '<hr style="border-top: 1px solid #e8e8e8; margin: 16px 0;" />',
              );

            Swal.fire({
              title: this.translate.instant('Conflict Detected'),
              html: `
                <p>${this.translate.instant(
                  'Overlapping appointments found',
                )}:</p>
                ${conflictText}
              `,
              icon: 'warning',
              width: 600,
              showCancelButton: true,
              confirmButtonText: this.translate.instant('Proceed anyway'),
              confirmButtonColor: COLORS.green,
              cancelButtonText: this.translate.instant('Cancel'),
            }).then((result) => {
              if (result.isConfirmed) {
                this.calendarEventService.setCalendarVisible(false);
                this.handleEventChange(event, updateType);
              } else {
                revertFn();
              }
            });
          }
        },
        error: () => {
          this.swalToastService.toastError(
            this.translate.instant('Unable to check for conflicts'),
          );
          revertFn();
        },
      });
  }

  handleEventDrop(droppedItem: any) {
    const event = droppedItem.event;
    const isRecurrent = event.extendedProps?.['recurrentTypeId'] !== 1;

    if (isRecurrent) {
      Swal.fire({
        title: this.translate.instant('Careful'),
        text:
          this.translate.instant(
            'You are about to update a slot from this repeated appointment',
          ) +
          ': ' +
          event.title,
        input: 'radio',
        inputOptions: {
          1: this.translate.instant('This slot only'),
          2: this.translate.instant('This and all follow-up slots'),
          3: this.translate.instant('All slots'),
        },
        customClass: {
          input: 'custom-swal2-radio',
        },
        inputValidator: (value) =>
          value
            ? null
            : this.translate.instant('You need to choose one option'),
        width: 1000,
        showCancelButton: true,
        confirmButtonText: this.translate.instant('Confirm'),
        confirmButtonColor: COLORS.green,
        cancelButtonText: this.translate.instant('Cancel'),
        // preConfirm: (selectedValue) => parseInt(selectedValue, 10),
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          this.calendarEventService.setCalendarVisible(false);
          this.checkAndHandleConflictBeforeUpdate(event, result.value, () => {
            droppedItem.revert();
            this.calendarEventService.setCalendarVisible(true);
          });
        } else {
          droppedItem.revert();
          this.calendarEventService.setCalendarVisible(true);
        }
      });
    } else {
      this.calendarEventService.setCalendarVisible(false);
      this.checkAndHandleConflictBeforeUpdate(event, 0, () => {
        droppedItem.revert();
        this.calendarEventService.setCalendarVisible(true);
      });
    }
  }

  handleEventResize(resizeInfo: any) {
    const event = resizeInfo.event;
    const isRecurrent = event.extendedProps?.['recurrentTypeId'] !== 1;

    if (isRecurrent) {
      Swal.fire({
        title: this.translate.instant('Careful'),
        text:
          this.translate.instant(
            'You are about to resize a slot from this repeated appointment',
          ) +
          ': ' +
          event.title,
        // Uncomment when backend logic for all usecases is fixed
        input: 'radio',
        inputOptions: {
          1: this.translate.instant('This slot only'),
          2: this.translate.instant('This and all follow-up slots'),
          3: this.translate.instant('All slots'),
        },
        customClass: {
          input: 'custom-swal2-radio',
        },
        inputValidator: (value) => {
          return value
            ? null
            : this.translate.instant('You need to choose one option');
        },
        width: 1000,
        showCancelButton: true,
        confirmButtonText: this.translate.instant('Confirm'),
        confirmButtonColor: COLORS.green,
        cancelButtonText: this.translate.instant('Cancel'),
        // Uncomment when backend logic for all usecases is fixed
        // preConfirm: (selectedValue) => {
        //   return parseInt(selectedValue, 10);
        // },
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          this.calendarEventService.setCalendarVisible(false);
          this.handleEventChange(event, result.value);
        } else {
          // Revert the resize change
          if (typeof resizeInfo.revert === 'function') {
            resizeInfo.revert();
          } else {
            this.calendarEventService.setCalendarVisible(false);
            this.fetchBackendData(); // Fallback
          }
        }
      });
    } else {
      this.calendarEventService.setCalendarVisible(false);
      this.handleEventChange(event); // Regular update
    }
  }

  handleResourceSelect(selectedId: string | null) {
    // (ngModelChange) runs before [(ngModel)] writes the field, so set it here
    this.selectedResourceId = selectedId;
    this.applyResources();
  }

  handlePatientResourceFilter(filter: PatientResourceFilter): void {
    this.patientResourceFilter = filter;
    this.applyResources();
  }
}
